import { observer } from 'mobx-react';
import { Paper } from '@mui/material';
import { NetworkStatus } from '@apollo/client';
import { useRouteLoaderData } from 'react-router-dom';

import {
  CampContactInput,
  CampContactsDocument,
  CampContactUpdate,
  CampPermissionDocument,
  CampPermissionInput,
  CampPermissionUpdate,
  CampQuery,
  useCampPermissionQuery,
  useCreateCampContactMutation,
  useCreateCampPermissionMutation,
  useDeleteCampPermissionMutation,
  useUpdateCampPermissionMutation,
} from '../../../../../../generated/graphql';
import {
  CacheOperation,
  updateCacheWithCampId,
} from '../../../../../../utils/updateCacheCamp';
import { CustomDialog } from '../../../../../../components/common/CustomDialog';
import { ErrorLoading } from '../../../../../../components/common/ErrorLoading';
import { AutoTableModal } from '../../../../../../components/Modals/AutoTableModal';
import {
  TableComponent,
  TableHeader,
} from '../../../../../../components/Table/Table';
import { TableColumn } from '../../../../../../types/tableColumn';
import { useStores } from '../../../../../../stores';
import { InputType } from '../../../../../../types/inputTypes';

const headers: TableColumn[] = [
  {
    title: 'Namn',
    accessor: 'name',
    inputType: InputType.Text,
  },
  {
    title: 'Kod',
    accessor: 'code',
    inputType: InputType.Text,
  },
  {
    title: 'Ikon',
    accessor: 'icon',
    inputType: InputType.SelectIcon,
    values: [
      { id: 'MaterialIcons/accessibility', name: 'Människa' },
      { id: 'MaterialIcons/access-time-filled', name: 'Klocka' },
      { id: 'MaterialIcons/accessible', name: 'Rullstolsburen' },
      { id: 'MaterialIcons/account-balance-wallet', name: 'Plånbok' },
      { id: 'MaterialIcons/agriculture', name: 'Traktor' },
      { id: 'MaterialIcons/airplanemode-active', name: 'Flygplan' },
      { id: 'MaterialIcons/airport-shuttle', name: 'Buss' }, 
      { id: 'MaterialIcons/all-inclusive', name: 'Oändligt' }, 
      { id: 'MaterialIcons/alternate-email', name: '@' }, 
      { id: 'MaterialIcons/anchor', name: 'Ankare' }, 
      { id: 'MaterialIcons/android', name: 'Android' }, 
      { id: 'MaterialIcons/announcement', name: 'Pratbubbla' }, 
      { id: 'MaterialIcons/assignment-ind', name: 'Badge' }, 
      { id: 'MaterialIcons/attachment', name: 'Gem' },   
      { id: 'MaterialIcons/audiotrack', name: 'Musik' },   
      { id: 'MaterialIcons/auto-awesome', name: 'Awesome' },   
      { id: 'MaterialIcons/auto-stories', name: 'Bok' },   
      { id: 'MaterialIcons/back-hand', name: 'Hand' }, 
      { id: 'MaterialIcons/bakery-dining', name: 'Croissant' }, 
      { id: 'MaterialIcons/battery-charging-full', name: 'Batteri' }, 
      { id: 'MaterialIcons/beach-access', name: 'Parasoll' }, 
      { id: 'MaterialIcons/bed', name: 'Säng' }, 
      { id: 'MaterialIcons/bedtime', name: 'Måne' }, 
      { id: 'MaterialIcons/bug-report', name: 'Insekt' }, 
      { id: 'MaterialIcons/cake', name: 'Tårta' }, 
      { id: 'MaterialIcons/phone', name: 'Telefon' }, 
      { id: 'MaterialIcons/camera-alt', name: 'Kamera' }, 
      { id: 'MaterialIcons/campaign', name: 'Megafon' }, 
      { id: 'MaterialIcons/celebration', name: 'Party' },                                                                        
      { id: 'MaterialIcons/church', name: 'Kyrka' }, 
      { id: 'MaterialIcons/child-friendly', name: 'Barnvagn' }, 
      { id: 'MaterialIcons/coffee', name: 'Kaffe' }, 
      { id: 'MaterialIcons/computer', name: 'Dator' }, 
      { id: 'MaterialIcons/construction', name: 'Verktyg' }, 
      { id: 'MaterialIcons/cookie', name: 'Kaka' }, 
      { id: 'MaterialIcons/dining', name: 'Bestick' }, 
      { id: 'MaterialIcons/fire-truck', name: 'Brandbil' }, 
      { id: 'MaterialIcons/gpp-good', name: 'Sköld' }, 
      { id: 'MaterialIcons/grade', name: 'Stjärna' }, 
      { id: 'MaterialIcons/group', name: 'Grupp' }, 
      { id: 'MaterialIcons/hot-tub', name: 'Badkar' }, 
      { id: 'MaterialIcons/local-florist', name: 'Blomma' }, 
      { id: 'MaterialIcons/local-pizza', name: 'Pizza' }, 
      { id: 'MaterialIcons/lunch-dining', name: 'Hamburgare' }, 
      { id: 'MaterialIcons/reddit', name: 'Alien' }, 
      { id: 'MaterialIcons/rocket-launch', name: 'Raket' }, 
      { id: 'MaterialIcons/roller-skating', name: 'Rullskridsko' }, 
      { id: 'MaterialIcons/save', name: 'Spara' }, 
      { id: 'MaterialIcons/savings', name: 'Spargris' }, 
      { id: 'MaterialIcons/settings', name: 'Kugghjul' }, 
      { id: 'MaterialIcons/sports-esports', name: 'Handkontroll' }, 
      { id: 'MaterialIcons/vpn-key', name: 'Nyckel' }, 
      { id: 'MaterialIcons/wb-cloudy', name: 'Moln' }, 
      { id: 'MaterialIcons/wb-sunny', name: 'Sol' }, 
      { id: 'MaterialIcons/whatshot', name: 'Eld' }, 
      { id: 'MaterialIcons/water-drop', name: 'Vatten' }, 
    ]
  },
];

export const AppPermissionsPage = observer(function AppPermissionsPage() {
  const stores = useStores();
  const modalStore = stores.modalStore;
  const camp = useRouteLoaderData('camp') as CampQuery['camp'];
  const camp_id = camp.camp_id;
  const { data, error, loading, refetch, networkStatus } = useCampPermissionQuery(
    { variables: { camp_id } },
  );
  const [deleteItem] = useDeleteCampPermissionMutation();

  const refetching = networkStatus === NetworkStatus.refetch;

  const [updateItem] = useUpdateCampPermissionMutation();

  if (!data) {
    return <ErrorLoading {...{ loading, error }} />;
  }

  return (
    <>
      <Paper>
        <TableHeader
          title={'Behörigheter'}
          onAddItem={() => {
            modalStore.addItem();
          }}
        />
        {!!data.campPermissions && (
          <TableComponent
            headers={headers}
            rows={data.campPermissions}
            showEdit={true}
            onUpdateRow={(id, accessor, newValue) => {
              return updateItem({
                variables: {
                  campPermission: {
                    event_id: camp_id,
                    id: id as number,
                    [accessor]: newValue as string,
                  },
                },
              });
            }}
            onEdit={(row) => modalStore.updateItem(row)}
            onDelete={(row) => {
              deleteItem({
                variables: {
                  camp_id,
                  campPermissionId: row.id as number,
                },
                update: (proxy: any) =>
                  updateCacheWithCampId({
                    query: CampPermissionDocument,
                    proxy,
                    method: CacheOperation.DELETE,
                    camp_id: camp_id!,
                    mutationResultObject: null,
                    id: row.id,
                  }),
              });
            }}
          />
        )}

        <AddItem camp_id={camp_id!} headers={headers} />
        <EditItem camp_id={camp_id!} headers={headers} />
      </Paper>
    </>
  );
});

const AddItem = observer(
  ({ camp_id, headers }: { camp_id: number; headers: TableColumn[] }) => {
    const stores = useStores();
    const modalStore = stores.modalStore;

    const [createItem] = useCreateCampPermissionMutation();

    const close = modalStore.closeAddItemModal;

    return (
      <CustomDialog open={modalStore.showingAddItemModal} onClose={close}>
        <AutoTableModal
          title="behörighet"
          headers={headers}
          existingItem={{}}
          onCancel={close}
          onSubmit={async (data) => {
            data.event_id = camp_id;
            await createItem({
              variables: {
                campPermission: data as CampPermissionInput,
              },
              update: (
                proxy: any,
                mutationResult: { data: { createCampPermission: any } },
              ) => {
                updateCacheWithCampId({
                  query: CampPermissionDocument,
                  proxy,
                  method: CacheOperation.CREATE,
                  camp_id,
                  mutationResultObject: mutationResult.data.createCampPermission,
                });
              },
            });
            close();
          }}
        />
      </CustomDialog>
    );
  },
);

const EditItem = observer(
  ({ camp_id, headers }: { camp_id: number; headers: TableColumn[] }) => {
    const stores = useStores();
    const modalStore = stores.modalStore;
    const [updateItem] = useUpdateCampPermissionMutation();

    const close = modalStore.closeUpdateItemModal;

    return (
      <CustomDialog open={modalStore.showingUpdateItemModal} onClose={close}>
        <AutoTableModal
          title="behörighet"
          headers={headers}
          existingItem={modalStore.existingItem}
          onCancel={close}
          onSubmit={(data) => {
            data.event_id = camp_id;
            data.id = modalStore.existingItem.id;
            updateItem({
              variables: {
                campPermission: data as CampPermissionUpdate,
              },
            });
            close();
          }}
        />
      </CustomDialog>
    );
  },
);
